import * as GC from '@grapecity/spread-sheets';
import { IProductTab } from '@models/product';
import { clone } from '../arrays';
import { CanDeactivateGuard } from '@guards/can-deactivate-guard.service';
import { ACTION_BUTTON_TYPES, setIconToActionButtonCell } from '@components/settings/product-setup/product-setup-spread';
import { colours } from './spread-settings';
import Swal from 'sweetalert2';
import { MenuService } from '@services/menu.service';

export class SpreadContextMenu {
  private static _allOptions: GC.Spread.Sheets.ContextMenu.IMenuItemData[] = null;

  static get allOptions(): GC.Spread.Sheets.ContextMenu.IMenuItemData[] {
    if (SpreadContextMenu._allOptions === null) {
      const contextMenu = new GC.Spread.Sheets.ContextMenu.ContextMenu();
      SpreadContextMenu._allOptions = clone(contextMenu.menuData);
    }
    return SpreadContextMenu._allOptions;
  }

  static setSimpleCopy(book: GC.Spread.Sheets.Workbook) {
    const enabledCommands = ['gc.spread.copy'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => enabledCommands.includes(menuItem.name));
    return this;
  }

  static disableHideRows(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.hideRows', 'gc.spread.unhideRows'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableHideColumns(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.hideColumns', 'gc.spread.unhideColumns'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableInsertRows(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowInsertRows = false;
    const disabledCommands = ['gc.spread.insertRows'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableDeleteRows(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowDeleteRows = false;

    const disabledCommands = ['gc.spread.deleteRows'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static enableDeleteRows(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowDeleteRows = true;

    const option = SpreadContextMenu.allOptions.find(({ name }) => name === 'gc.spread.deleteRows');
    book.contextMenu.menuData.push({ type: 'separator' }, option);
    return this;
  }

  static enableInsertRowsAboveAndBelow(book: GC.Spread.Sheets.Workbook) {
    const insertAbove = {
      text: 'Insert Rows Above',
      name: 'gc.spread.insertRows',
      command: 'gc.spread.contextMenu.insertRows',
      workArea: 'rowHeader'
    };
    const insertBelow = {
      text: 'Insert Rows Below',
      name: 'addRowBelowCmd',
      workArea: 'rowHeader',
      command: 'addRowBelowCmd'
    };

    book.contextMenu.menuData.push({ type: 'separator' }, insertAbove, insertBelow);
    return this;
  }

  static disableInsertColumns(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowInsertColumns = false;
    const disabledCommands = ['gc.spread.insertColumns'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableDeleteColumns(book: GC.Spread.Sheets.Workbook) {
    const sheet: GC.Spread.Sheets.Worksheet = book.getActiveSheet();
    sheet.options.protectionOptions.allowDeleteColumns = false;
    const disabledCommands = ['gc.spread.deleteColumns'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableClearContent(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.clearContents'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableInsertComments(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.insertComment'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableFilter(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.filter'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableSort(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.sort'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableInsertComment(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.insertComment'];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }

  static disableSpreadTabCommands(book: GC.Spread.Sheets.Workbook) {
    const disabledCommands = ['gc.spread.insertSheet', 'gc.spread.deleteSheet','gc.spread.hideSheet','gc.spread.unhideSheet']; //
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));
    return this;
  }
  static setCopyCutPaste(book: GC.Spread.Sheets.Workbook) {
    // Clean the context menu to allow copy, cut, paste only
    const allowedPasteCommand = 'gc.spread.pasteValues';
    const allowedCommands = ['gc.spread.copy', 'gc.spread.cut', allowedPasteCommand];
    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => allowedCommands.includes(menuItem.name));

    const pasteOption = book.contextMenu.menuData.find(c => c.name === allowedPasteCommand);
    if (pasteOption) {
      delete pasteOption.group;

      pasteOption.text = 'Paste (Use Ctrl + V instead)';
      pasteOption.disable = true;
    }

    return this;
  }

  static enableSimplePaste(book: GC.Spread.Sheets.Workbook) {
    const allowedPasteCommand = 'gc.spread.pasteValues';
    const disabledCommands = ['gc.spread.pasteOptions', 'gc.spread.pasteAll', 'gc.spread.pasteFormula', 'gc.spread.pasteFormatting'];

    book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => !disabledCommands.includes(menuItem.name));

    const pasteOption = book.contextMenu.menuData.find(c => c.name === allowedPasteCommand);
    if (pasteOption) {
      delete pasteOption.group;
      pasteOption.text = 'Paste';
    }
    return this;
  }

  static setLockedContextMenu(book: GC.Spread.Sheets.Workbook) {
    // Clean the context menu to allow copy, cut, paste only
    this.setSimpleCopy(book);
    // const allowedCommands = ['gc.spread.copy'];
    // book.contextMenu.menuData = book.contextMenu.menuData.filter(menuItem => allowedCommands.includes(menuItem.name));
    return this;
  }

  // COPY, CUT, PASTE + OPTIONAL INSERT/DELETE ROWS
  static setUnlockedContextMenu(book: GC.Spread.Sheets.Workbook, enableInsertRows = true, enableDeleteRows = true) {
    // Clean the context menu to allow copy, cut, paste only
    const allowedPasteCommand = 'gc.spread.pasteValues';
    const allowedCommands = ['gc.spread.copy', 'gc.spread.cut', allowedPasteCommand];
    // Insert and delete rows
    if (enableInsertRows) {
      allowedCommands.push('gc.spread.insertRows');
    }
    if (enableDeleteRows) {
      allowedCommands.push('gc.spread.deleteRows');
    }

    const pasteOption = book.contextMenu.menuData.find(c => c.name === allowedPasteCommand);
    if (pasteOption) {
      delete pasteOption.group;
      pasteOption.text = 'Paste (Use Ctrl + V instead)';
      pasteOption.disable = true;
    }
    book.contextMenu.menuData = [...book.contextMenu.menuData.filter(menuItem => allowedCommands.includes(menuItem.name))];
    return this;
  }

  // static deleteTab(book: GC.Spread.Sheets.Workbook, productTabs: IProductTab[], onTabDeleted, this) {
  //   const deleteCurrentTabCmd = {
  //     canUndo: true,
  //     execute: (context: GC.Spread.Sheets.Workbook, options = { productTabs }, isUndo) => {
  //       console.dir(options.productTabs.length);
  //       console.log(`Sheet Name ${context.getActiveSheet().name()}\n${options.productTabs}`);
  //     }
  //   };
  //   const commandManager = book.commandManager();
  //   commandManager.register('deleteCurrentTab', deleteCurrentTabCmd);
  //   var openDialog = {
  //     text: 'Delete Tab',
  //     name: 'openDialog',
  //     command: async () => {
  //       const rowCount = book.getActiveSheet().getRowCount();
  //       const tabName = book.getActiveSheet().name();

  //       const promise: Promise<boolean> = new Promise<boolean>((resolve, reject) => {
  //         Swal.fire({
  //           title: `You are about to delete "${tabName}" \nThis will permanently delete the tab and all associated products.`,
  //           text: `Please click cancel to return to the page or continue to delete ${tabName}.`,
  //           type: 'warning',
  //           showCancelButton: true,
  //           confirmButtonText: 'Continue',
  //           cancelButtonText: 'Cancel',
  //           reverseButtons: true,
  //           allowEscapeKey: true
  //         })
  //           .then(result => {
  //             if (result && result.value) {
  //               resolve(true);
  //             } else {
  //               resolve(false);
  //             }
  //           })
  //           .catch(err => {
  //             reject(true);
  //           });
  //       });

  //       const deleteTab = await promise;
  //       if (deleteTab) {
  //         const tabName = book.getActiveSheet().name();
  //         onTabDeleted(tabName, this);
  //         // const tabIndex = productTabs.findIndex(tab => tab.Name === tabName);
  //         // productTabs[tabIndex].Name = null;
  //         // productTabs[tabIndex].IsPublished = null;
  //         // book
  //         //   .getActiveSheet()
  //         //   .getCell(1, 1)
  //         //   .locked(true);
  //         // book.getActiveSheet().options.sheetTabColor = colours.RED;
  //         // book
  //         //   .getActiveSheet()
  //         //   .getRange(1, 0, rowCount, 11)
  //         //   .foreColor(colours.RED)
  //         //   .backColor(colours.LIGHT_RED)
  //         //   .textDecoration(GC.Spread.Sheets.TextDecorationType.lineThrough)
  //         //   .locked(true);

  //         // canDeactivateGuardService.canDeactivateTab = false;

  //         // menuService.dialogSaveButton = {
  //         //   page: 'product-setup',
  //         //   valid: true,
  //         //   source: `product-setup enableSaveButton`,
  //         //   unsavedChanges: true,
  //         //   clicked: false
  //         // };

  //         // menuService.dialogSaveButton = {
  //         //   page: 'product-setup',
  //         //   valid: false,
  //         //   source: `product-setup enableDiscardButton`,
  //         //   unsavedChanges: true,
  //         //   clicked: false
  //         // };
  //       }
  //       return productTabs;
  //     },
  //     workArea: 'sheetTab'
  //   };

  //   book.contextMenu.menuData,
  //     (p, v) => {
  //       if (v.name === 'openDialog') {
  //         //openDialog is a command's name
  //         book.contextMenu.menuData.splice(p, 1);
  //       }
  //     };
  //   book.contextMenu.menuData.push(openDialog);
  //   return this;
  // }
}
